import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { GppMaybeRounded } from '@mui/icons-material';
import { useUser } from '../../../contexts/UserContext';
import { useNetwork, ApiError } from '../../../utils/network';
import { useShowSnackbar } from '../../../utils/snackbar';
import { PT } from '../../../components/portalUi';

/* "A two-factor reset was requested for your account." The emails are the owner's
 * defence against somebody else's request, and the threat model says that somebody
 * may hold the inbox and delete them. A member who is still signed in — a laptop with
 * "remember me" — would otherwise never know. So the portal asks the server once on
 * arrival and, if a request is open, says so and offers "that wasn't me", which a
 * live session is entitled to: whoever filled in the form had the password but no
 * second factor to sign in with.
 *
 * The same answer carries securityHoldUntil, so a session that was already open
 * when a reset went through learns about the hold without signing in again. */
type Standing = {
  openRequest: { id: number; source: 'member' | 'admin'; state: string; createdAt: string; readyAt: string } | null;
  securityHoldUntil: string | null;
};

const when = (iso: string) =>
  new Date(iso).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });

export const TwoFactorResetRequestNotice = () => {
  const { user, setUser } = useUser();
  const { get, post } = useNetwork();
  const { showSnackbar } = useShowSnackbar();
  const [standing, setStanding] = useState<Standing | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    get('/user/two-factor-reset', true)
      .then((res: Standing) => {
        if (!alive || !res) return;
        setStanding(res);
        const hold = res.securityHoldUntil ?? null;
        setUser((u) => (u && (u.securityHoldUntil ?? null) !== hold ? { ...u, securityHoldUntil: hold } : u));
      })
      .catch(() => {
        /* nothing to say: the notice is extra, not the only defence */
      });
    return () => {
      alive = false;
    };
    // once per portal mount, deliberately
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const request = standing?.openRequest;
  if (!request || !user) return null;

  const cancel = async () => {
    setBusy(true);
    try {
      const res = await post('/user/two-factor-reset/cancel', {}, true);
      showSnackbar(res?.message || 'The reset request has been cancelled.', res?.cancelled ? 'success' : 'info');
      setStanding((s) => (s ? { ...s, openRequest: null } : s));
    } catch (err) {
      showSnackbar((err as ApiError)?.data?.message || 'Could not cancel the request. Please try again.', 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box
      role="status"
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5,
        mx: 2,
        mt: 2,
        mb: 1,
        p: { xs: 1.5, sm: 2 },
        borderRadius: '14px',
        border: '1px solid rgba(255,107,107,0.5)',
        background: 'rgba(255,107,107,0.08)'
      }}
    >
      <GppMaybeRounded sx={{ color: '#ff8a80', flexShrink: 0, mt: '1px' }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 14.5, fontWeight: 700, color: PT.text, lineHeight: 1.45 }}>
          {request.source === 'admin'
            ? `Our support team opened a request to reset the two-factor authentication on your account on ${when(request.createdAt)}.`
            : `A reset of the two-factor authentication on your account was requested on ${when(request.createdAt)}, using your email and password.`}
        </Typography>
        <Typography sx={{ mt: 0.5, fontSize: 13.5, color: PT.textMuted, lineHeight: 1.45 }}>
          Nothing changes before {when(request.readyAt)}. If this was you, there is nothing to do. If it was not,
          cancel it now — and change your password, because whoever asked knew it.
        </Typography>
        <Button
          size="small"
          variant="contained"
          color="error"
          onClick={cancel}
          disabled={busy}
          startIcon={busy ? <CircularProgress size={14} color="inherit" /> : undefined}
          sx={{ mt: 1.25, textTransform: 'none', fontWeight: 800, borderRadius: '10px' }}
        >
          {busy ? 'Cancelling…' : 'That wasn’t me — cancel it'}
        </Button>
      </Box>
    </Box>
  );
};
